import { useCallback, useEffect, useMemo } from "react";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { IAndITimePeriodsValue } from "@/config/date";
import { StructureFilterParams } from "@/utils/filters";
import { infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";

export type ColumnMaxApi = { data: Dryp.IAndISummaryList | null; loading: boolean };

export type IAndIListColumnApis = {
	infiltrationVolume: ColumnMaxApi;
	inflowVolume: ColumnMaxApi;
	infiltrationFlowPerLength: ColumnMaxApi;
	inflowFlowPerLength: ColumnMaxApi;
};

export type FetchColumnMaxes = (timePeriod: IAndITimePeriodsValue, structureFilters: StructureFilterParams) => void;

export const useIAndIListColumnMaxes = (): { apis: IAndIListColumnApis; fetchMaxes: FetchColumnMaxes } => {
	const infiltrationVolumeMaxApi = useAsyncStateStore(infiltrationAndInflow.getSummaries, { debounce: 300 });
	const inflowVolumeMaxApi = useAsyncStateStore(infiltrationAndInflow.getSummaries, { debounce: 300 });
	const infiltrationFlowPerLengthMaxApi = useAsyncStateStore(infiltrationAndInflow.getSummaries, { debounce: 300 });
	const inflowFlowPerLengthMaxApi = useAsyncStateStore(infiltrationAndInflow.getSummaries, { debounce: 300 });

	const fetchMaxes = useCallback<FetchColumnMaxes>(
		(timePeriod, structureFilters) => {
			const pagination = { pageNumber: 0, pageSize: 1 };
			infiltrationVolumeMaxApi.fetch(
				pagination,
				timePeriod,
				{ sort: "infiltrationVolume", direction: "desc" },
				structureFilters,
			);
			inflowVolumeMaxApi.fetch(
				pagination,
				timePeriod,
				{ sort: "inflowVolume", direction: "desc" },
				structureFilters,
			);
			infiltrationFlowPerLengthMaxApi.fetch(
				pagination,
				timePeriod,
				{ sort: "iAndIBreakdown_infiltration_flowPerPipeLength", direction: "desc" },
				structureFilters,
			);
			inflowFlowPerLengthMaxApi.fetch(
				pagination,
				timePeriod,
				{ sort: "iAndIBreakdown_inflow_flowPerPipeLength", direction: "desc" },
				structureFilters,
			);
		},
		[infiltrationVolumeMaxApi, inflowVolumeMaxApi, infiltrationFlowPerLengthMaxApi, inflowFlowPerLengthMaxApi],
	);

	const apis = useMemo<IAndIListColumnApis>(
		() => ({
			infiltrationVolume: infiltrationVolumeMaxApi,
			inflowVolume: inflowVolumeMaxApi,
			infiltrationFlowPerLength: infiltrationFlowPerLengthMaxApi,
			inflowFlowPerLength: inflowFlowPerLengthMaxApi,
		}),
		[infiltrationVolumeMaxApi, inflowVolumeMaxApi, infiltrationFlowPerLengthMaxApi, inflowFlowPerLengthMaxApi],
	);

	return { apis, fetchMaxes };
};

export const useRefetchColumnMaxes = (
	fetchMaxes: FetchColumnMaxes,
	timePeriod: IAndITimePeriodsValue,
	structureFilters: StructureFilterParams,
) => {
	const key = JSON.stringify({ timePeriod, structureFilters });
	useEffect(() => {
		fetchMaxes(timePeriod, structureFilters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);
};

export type ColumnUnits = { volume?: string; flowPerLength?: string };

export const getColumnUnits = (items: Dryp.IAndISummary[]): ColumnUnits => ({
	volume: items
		.map(item => item.parts?.infiltration.volume?.unit.symbol ?? item.parts?.inflow.volume?.unit.symbol)
		.find(Boolean),
	flowPerLength: items
		.map(
			item =>
				item.parts?.infiltration.flowPerLength?.unit.symbol ?? item.parts?.inflow.flowPerLength?.unit.symbol,
		)
		.find(Boolean),
});
